import { Component, ViewChild, ElementRef, AfterViewChecked, OnDestroy, Input, HostBinding } from '@angular/core';
import { Socket } from 'socket.io-client';
import { HttpService } from '../../http.service';
import { ActivatedRoute, Router } from '@angular/router';
import { TemplatePopupComponent } from '../template-popup/template-popup.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DomSanitizer } from '@angular/platform-browser';
import { MessagingService } from 'src/app/messaging.service';

@Component({
  selector: 'app-chat',
  templateUrl: './chat.component.html',
  styleUrls: ['./chat.component.scss']
})
export class ChatComponent implements AfterViewChecked, OnDestroy {

  @ViewChild('messageContainer') messageContainer!: ElementRef;
  
  public message: string = "";
  public messages: any[] = [];
  public chats: any[] = [];
  public chat: any = {};
  public page: number = 1;
  public chat_page: number = 1;
  public isLoading: boolean = false;
  public hasMoreMessages: boolean = true;
  private socket: Socket | null = null;
  public chat_uuid: string | null = null;
  public company_uuid: string | null = null;
  private shouldScrollToBottom: boolean = true;
  private isInitialLoad: boolean = true;
  private isLoadingOldMessages: boolean = false;
  public filter: string = 'unread';
  public channelFilter: string = 'all';
  public channelIdFilter: number | null = null;
  public searchTerm: string = '';
  private searchDebounce: any = null;
  public show_end_chat: boolean = false;
  public hasMoreChats: boolean = true;
  public loading_more_chats: boolean = false;
  public chatLoadFailed: boolean = false;
  private newMessageObserver: any;

  public volume: number = 0.5;
  public reminderMinutes: number = 15;
  public reminderEnabled: boolean = true;
  public reminderChats: any[] = [];
  public showReminderPopup: boolean = false;
  public is_sending: boolean = false;
  private reconnectObserver: any;

  // Dynamic channels
  public channels: any[] = [];
  public editingChannelId: number | null = null;
  public editingChannelName: string = '';

  // Embedded mode (rendered inside the panel-shell offcanvas instead of full page)
  @Input() @HostBinding('class.embedded') embedded: boolean = false;

  constructor(private http: HttpService, private activatedRoute: ActivatedRoute, private ngbModal: NgbModal, protected _sanitizer: DomSanitizer, private router: Router, private messagingService: MessagingService) {
    // this.init_socket();

    // Handle channel query param (e.g. from /whatsapp redirect)
    this.activatedRoute.queryParams.subscribe((params: any) => {
      if (params.channel && params.channel !== this.channelFilter) {
        this.channelFilter = params.channel;
        this.chat_page = 1;
        this.hasMoreChats = true;
        this.chats = [];
        this.getChats();
      }
    });

    this.getChats();
    this.loadChannels();

    this.initVolume();
    this.initReminderSettings();

    this.activatedRoute.params.subscribe(async (v:any) => {
      this.page = 1;
      this.messages = [];
      this.hasMoreMessages = true;
      this.isInitialLoad = true;
      this.shouldScrollToBottom = true;

      if(v.chat_uuid){
        this.chat_uuid = v.chat_uuid;
        await this.getChat();
      }
    })

    this.http.getSelectedCompany().subscribe(company_uuid => {
      this.company_uuid = company_uuid;
    });

    this.newMessageObserver = this.http.getNewMessage().subscribe(async (message: any) => {

      if(!message) {
        return;
      }

      // Beep is now handled centrally in http.service socket handler — no duplicate here

      let if_in_existing_chats = this.chats.find((chat: any) => chat.room_id == message.room);

      if(if_in_existing_chats) {
        if_in_existing_chats.last_message = message.timestamp;
        if(message.room != this.chat_uuid){
          if_in_existing_chats.unread = if_in_existing_chats.unread + 1;
          if(message.user_type === 'user' && !if_in_existing_chats.unread_since) {
            if_in_existing_chats.unread_since = new Date();
          }
        }
      } else {
        await this.getNewChats(message);
      }

      if(message.room == this.chat_uuid){
        this.shouldScrollToBottom = true;
        if(message.user_type == 'user'){
          this.show_end_chat = true;
        }
        // Avoid duplicate if already pushed locally from sendMessage
        if (!this.messages.find((m: any) => m.uuid === message.uuid)) {
          this.messages.push(message);
        }

        await this.markAsRead(message.room);
      }

      this.chats.sort((a: any, b: any) => {
        if (a.pinned && !b.pinned) return -1;
        if (!a.pinned && b.pinned) return 1;
        return new Date(b.last_message).getTime() - new Date(a.last_message).getTime();
      });
    });

    // Refresh chat list on socket reconnect to catch missed messages
    this.reconnectObserver = this.http.getSocketReconnected().subscribe((reconnected: boolean) => {
      if (reconnected) {
        this.chat_page = 1;
        this.hasMoreChats = true;
        this.chats = [];
        this.getChats();

        if (this.chat_uuid) {
          this.page = 1;
          this.messages = [];
          this.hasMoreMessages = true;
          this.isInitialLoad = true;
          this.shouldScrollToBottom = true;
          this.getChat();
        }
      }
    });

    this.http.getChatReminder().subscribe((overdue: any[]) => {
      this.reminderChats = overdue;
      this.showReminderPopup = true;
    });
  }

  ngAfterViewChecked() {
    if (this.shouldScrollToBottom && this.messageContainer) {
      this.scrollToBottom();
      this.shouldScrollToBottom = false;
    }
  }

  ngOnDestroy() {
    this.newMessageObserver?.unsubscribe();
    this.reconnectObserver?.unsubscribe();
    if (this.searchDebounce) clearTimeout(this.searchDebounce);
  }

  private scrollToBottom(): void {
    try {
      const element = this.messageContainer.nativeElement;
      element.scrollTop = element.scrollHeight;
    } catch (err) {
      console.error('Error scrolling to bottom:', err);
    }
  }

  public html(val:any) {
    // return this._sanitizer.bypassSecurityTrustHtml(val)
    return val;
  }

  initVolume(){
    this.volume = parseFloat(localStorage.getItem('volume') || '0.5');
  }

  initReminderSettings() {
    const saved = localStorage.getItem('chat_reminder_minutes');
    if (saved) this.reminderMinutes = parseInt(saved);
    const enabled = localStorage.getItem('chat_reminder_enabled');
    if (enabled !== null) this.reminderEnabled = enabled === 'true';
  }

  dismissReminder() {
    this.showReminderPopup = false;
    this.reminderChats.forEach((c: any) => { this.http.clearUnreadChat(c.room_id); });
    this.reminderChats = [];
  }

  goToReminderChat(chat: any) {
    this.dismissReminder();
    this.loadChat(chat);
  }

  saveReminderSettings() {
    localStorage.setItem('chat_reminder_minutes', this.reminderMinutes.toString());
    localStorage.setItem('chat_reminder_enabled', this.reminderEnabled.toString());
  }

  changeVolume(event: any){
    this.volume = parseFloat(event.target.value);
    localStorage.setItem('volume', this.volume.toString());
  }

  async markAsRead(room_id: string){
    let chat_item = this.chats.find((c: any) => c.room_id == room_id);
    if(chat_item){
      chat_item.unread = 0;
      chat_item.unread_since = null;
    }
    this.http.clearUnreadChat(room_id);
    this.reminderChats = this.reminderChats.filter((c: any) => c.room_id !== room_id);
    if (this.reminderChats.length === 0) this.showReminderPopup = false;

    await this.http.post('/admin/chat/mark-as-read', {
      room_id: room_id
    });
  }

  async loadOldMessages(): Promise<void> {
    if (!this.chat_uuid || this.isLoadingOldMessages || !this.hasMoreMessages) {
      return;
    }

    this.isLoadingOldMessages = true;
    this.shouldScrollToBottom = false;

    try {
      const rtn: any = await this.http.post('/admin/chat/get-message-by-room-page', {
        chat_uuid: this.chat_uuid,
        page: this.page + 1,
        last_message: this.messages[0]
      });

      if (rtn.messages && rtn.messages.length > 0) {
        // Store current scroll position and height
        const element = this.messageContainer.nativeElement;
        const scrollHeight = element.scrollHeight;
        const scrollTop = element.scrollTop;
        
        // Prepend old messages
        this.messages.unshift(...rtn.messages.reverse());
        this.page++;

        // Restore scroll position after new messages are added
        setTimeout(() => {
          const newScrollHeight = element.scrollHeight;
          element.scrollTop = newScrollHeight - scrollHeight + scrollTop;
        }, 100);

        if(rtn.messages.length < 20){
          this.hasMoreMessages = false;
        }
      } else {
        this.hasMoreMessages = false;
      }
    } catch (error) {
      console.error('Error loading old messages:', error);
    } finally {
      this.isLoadingOldMessages = false;
    }
  }

  async getChats() {
    this.loading_more_chats = true;
    this.chatLoadFailed = false;
    try {
      const rtn:any = await this.http.post('/admin/chat/get-messages', {filter: this.filter, page: this.chat_page, channel: this.channelFilter, channel_id: this.channelIdFilter, search: this.searchTerm});
      if (!Array.isArray(rtn?.chats)) {
        this.chatLoadFailed = true;
        return;
      }
      this.chats = [...this.chats, ...rtn.chats];
      this.hasMoreChats = rtn.chats.length >= 20;
      if (this.hasMoreChats) this.chat_page++;
    } catch {
      this.chatLoadFailed = true;
    } finally {
      this.loading_more_chats = false;
    }
  }

  async getChat(){
    let rtn:any = await this.http.post('/admin/chat/get-message-by-room', {
      chat_uuid: this.chat_uuid,
      page: this.page
    });
    this.chat = rtn.chat.chat;

    if(this.chat.messages.length > 0 && !this.chat.messages[rtn.chat.chat.messages.length - 1].admin_id && !this.chat.completed){
      this.show_end_chat = true;
    }

    this.messages = rtn.chat.chat.messages.reverse();
    // Check if there are more messages to load
    if (rtn.chat.chat.messages.length < 20) {
      this.hasMoreMessages = false;
    }
    
    // Scroll to bottom on initial load
    if (this.isInitialLoad) {
      setTimeout(() => {
        this.shouldScrollToBottom = true;
        this.isInitialLoad = false;
      }, 100);
    }
  }

  async getNewChats(message:any){
    let rtn:any = await this.http.post('/admin/chat/get-messages', {room_id: message.room, page: this.chat_page});

    if(rtn.chat){
      rtn.chat.unread = 1;
      this.chats.push(rtn.chat);
    }

  }

  endChat(){
    this.show_end_chat = false;
    this.http.post('/admin/chat/end-chat', {
      chat_uuid: this.chat_uuid
    })

    if(this.filter == 'unread'){
      let index = this.chats.findIndex((c: any) => c.room_id == this.chat_uuid);
      if(index != -1){
        this.chats.splice(index, 1);
      }
    }
  }

  backToList(){
    if (this.embedded) {
      this.chat_uuid = null;
      this.messages = [];
      this.show_end_chat = false;
    } else {
      this.router.navigate(['/lo', this.company_uuid, 'chat']);
    }
  }

  loadChat(chat: any){
    if (this.embedded) {
      // Offcanvas/embedded mode — open the conversation in place, no router navigation
      this.chat_uuid = chat.room_id;
      this.page = 1;
      this.messages = [];
      this.hasMoreMessages = true;
      this.isInitialLoad = true;
      this.shouldScrollToBottom = true;
      this.getChat();
    } else {
      this.router.navigate(['/lo', this.company_uuid, 'chat', chat.room_id]);
    }
    let chat_item = this.chats.find((c: any) => c.room_id == chat.room_id);

    if(chat_item && chat_item.unread > 0 && chat_item.unread <= 20){
      chat_item.unread = 0;
    } else if (chat_item && chat_item.unread > 20){
      chat_item.unread = chat_item.unread - 20;
    }
  }

  async sendMessage(message:any = null, chat_uuid:any = null){

    if((!this.chat_uuid && !chat_uuid) || (!this.message && !message)){
      return;
    }

    this.is_sending = true;

    const msgText = message || this.message;
    const targetRoom = chat_uuid || this.chat_uuid;

    let y:any = await this.http.post('/admin/chat/send-message', {
      message: msgText,
      chat_uuid: targetRoom
    });

    this.is_sending = false;

    if(!y.status){
      return;
    }

    // Push the sent message locally so it appears immediately
    if (targetRoom === this.chat_uuid && y.uuid) {
      // Pre-register in dedup so socket event won't duplicate
      this.http.markMessageSeen(y.uuid);

      const localMsg = {
        id: y.uuid,
        uuid: y.uuid,
        message: msgText,
        user: 'You',
        user_type: 'admin',
        timestamp: new Date(),
        type: 'chat',
        room: targetRoom,
        channel: this.chat?.channel || 'internal'
      };
      this.messages.push(localMsg);
      this.shouldScrollToBottom = true;
    }

    this.message = "";
    this.shouldScrollToBottom = true;
    this.show_end_chat = false;
      
  }

  deleteMessage(id: string){
    this.http.getSocket().emit('admin_delete_chat_message', {
      uuid: id
    }, (callback: any) => {
      if(!callback) {
        return;
      }
      let message = this.messages.find((message: any) => message.id == id);
      if(message){
        message.status = 99;
      }
    });
  }

  async pinnChat(room_id: string){
    await this.http.post('/admin/chat/pin-chat', {
      room_id: room_id
    });

    let chat = this.chats.find((chat: any) => chat.room_id == room_id);

    if(chat){
      chat.pinned = !chat.pinned;
    }

    // Sort chats to show pinned chats first
    this.chats.sort((a: any, b: any) => {
      if (a.pinned && !b.pinned) return -1;
      if (!a.pinned && b.pinned) return 1;
      // If both have same pin stat us, sort by last_message_timestamp
      return new Date(b.last_message).getTime() - new Date(a.last_message).getTime();
    });


  }


  sendImage(event: any) {
    let file = event.target.files[0];

    if (!file) {
      return;
    }

    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = async () => {
      const base64 = reader.result as string;

      this.is_sending = true;

      let y:any = await this.http.post('/admin/chat/send-image', {
        image: base64,
        chat_uuid: this.chat_uuid
      });

      this.is_sending = false;
  
      if(!y.status){
        return;
      }
  
      this.message = "";
      this.shouldScrollToBottom = true;
      this.show_end_chat = false;

      // chat/send-image
      // this.http.getSocket().emit('admin_chat_image', {
      //   image: base64,
      //   chat_uuid: this.chat_uuid
      // }, (callback: any) => {
      //   if(!callback) {
      //     return;
      //   }
      //   this.shouldScrollToBottom = true;
      // });
    };
  }
  sendVideo(event: any) {
    let file = event.target.files[0];

    if (!file) {
      return;
    }

    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const base64 = reader.result as string;
      this.http.getSocket().emit('admin_chat_video', {
        video: base64,
        chat_uuid: this.chat_uuid
      }, (callback: any) => {
        if(!callback) {
          return;
        }
        this.shouldScrollToBottom = true;
      });
    };
  }
  sendTemplate(){
    let modal = this.ngbModal.open(TemplatePopupComponent, {
      size: 'md'
    });

    modal.componentInstance.closeEvent.subscribe((template: any) => {
      if(template){
        this.sendMessage(template, this.chat_uuid);
        this.shouldScrollToBottom = true;
        modal.close();
      } else {
        modal.close();
      }
    });
  }

  filterChat(status: string){
    this.filter = status;
    this.chat_page = 1;
    this.hasMoreChats = true;
    this.chats = [];
    this.getChats();
  }

  // ── Player code search ──

  private reloadChats(){
    this.chat_page = 1;
    this.hasMoreChats = true;
    this.chats = [];
    this.getChats();
  }

  onSearchInput(){
    if (this.searchDebounce) clearTimeout(this.searchDebounce);
    this.searchDebounce = setTimeout(() => this.reloadChats(), 350);
  }

  clearSearch(){
    if (!this.searchTerm) return;
    this.searchTerm = '';
    if (this.searchDebounce) clearTimeout(this.searchDebounce);
    this.reloadChats();
  }

  // ── Channel Management ──

  async loadChannels() {
    const rtn: any = await this.http.post('/admin/chat/channel/list');
    this.channels = rtn?.channels || [];
  }

  startEditChannel(ch: any, event: Event) {
    event.stopPropagation();
    this.editingChannelId = ch.id;
    this.editingChannelName = ch.name;
  }

  cancelEditChannel(event?: Event) {
    if (event) event.stopPropagation();
    this.editingChannelId = null;
    this.editingChannelName = '';
  }

  async saveChannelName(ch: any, event?: Event) {
    if (event) event.stopPropagation();
    if (!this.editingChannelName.trim()) return;

    const rtn: any = await this.http.post('/admin/chat/channel/update', {
      id: ch.id,
      name: this.editingChannelName.trim()
    });

    if (rtn?.status) {
      ch.name = this.editingChannelName.trim();
    }

    this.editingChannelId = null;
    this.editingChannelName = '';
  }

  async deleteChannel(ch: any, event: Event) {
    event.stopPropagation();
    if (!confirm(`Delete channel "${ch.name}"? This will disconnect and remove it.`)) return;

    const rtn: any = await this.http.post('/admin/chat/channel/delete', { id: ch.id });
    if (rtn?.status) {
      this.channels = this.channels.filter((c: any) => c.id !== ch.id);
      // If we were filtering by this channel, reset to all
      if (this.channelFilter === ch.channel) {
        this.filterChannel('all');
      }
    }
  }

  getChannelIcon(channel: string): string {
    switch (channel) {
      case 'whatsapp': return 'whatsapp';
      case 'telegram': return 'telegram';
      case 'viber': return 'viber';
      case 'wechat': return 'wechat';
      default: return 'internal';
    }
  }

  onEnterKey(event: Event): void {
    const ke = event as KeyboardEvent;
    if (!ke.shiftKey) {
      ke.preventDefault();
      this.sendMessage();
    }
  }

  filterChannel(channel: string){
    this.channelFilter = channel;
    this.channelIdFilter = null;
    this.chat_page = 1;
    this.hasMoreChats = true;
    this.chats = [];
    this.getChats();
  }

  filterByChannelId(ch: any) {
    this.channelFilter = ch.channel;
    this.channelIdFilter = ch.id;
    this.chat_page = 1;
    this.hasMoreChats = true;
    this.chats = [];
    this.getChats();
  }
}
